import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import { Flex } from 'foundations-components/components/Flex';
import { Big } from 'foundations-components/transitional/components/Big';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { Link } from 'foundations-components/transitional/components/Link';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { View } from 'foundations-components/transitional/components/View';
import { getMaxAdditionalUnits, getQuantityPackSteppingFunction } from 'pricing-pages-lib/utils/contactsUtils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useEffect, useId, useState } from 'react';
import debounce from 'react-utils/debounce';
import { MONTHLY } from 'self-service-api/constants/Terms';
import { planBuilderQuantityUpdated } from '../../../actions/PlanBuilderActions';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { usePriceForQuantityPackQuantity } from '../../../hooks/usePagedQuantityPacks';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { minPurchasableContactsSelector } from '../../../selectors/ContactsSelectors';
import { planBuilderIsLoadingSelector, planBuilderPaymentTermForProductsSelector, planBuilderProductConfigurationsSelector } from '../../../selectors/PlanBuilderSelectors';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { CREATE_A_BUNDLE } from '../../../v2/constants/tracking/TrackingSources';
import { trackAdditionalUnitsPicker, trackModalInteraction } from '../../../v2/utils/tracking/trackingEventUtils';
import { isValidPaidProductLevel, isValidProductLevel } from '../../../v2/utils/typeguards';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import AsyncContactsPriceBreakdownModal from '../../common/pricing/AsyncContactsPriceBreakdownModal';
import { Divider } from '../SharedComponents';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const QuantityPickerContacts = ({
  additionalUnitSku
}) => {
  var _additionalUnitSku$in, _additionalUnitSku$in2;
  const dispatch = useAppDispatch();
  const productConfigurations = useAppSelector(planBuilderProductConfigurationsSelector);
  const additionalUnitSkuId = additionalUnitSku.skuId;

  // For now, we're opting to NOT show discounted prices in our units pickers,
  // so we're setting the queryParamTerm to MONTHLY
  const unitsPickerDisplayTerm = MONTHLY;
  const foundConfig = productConfigurations.find(({
    skuId
  }) => skuId === additionalUnitSkuId);
  const additionalUnitQuantity = foundConfig ? foundConfig.quantity : 0;
  const onChange = quantity => {
    dispatch(planBuilderQuantityUpdated({
      skuId: additionalUnitSkuId,
      quantity
    }));
  };
  const [priceBreakdownVisible, setPriceBreakdownVisible] = useState(false);
  const productId = additionalUnitSku.productId;
  const includedWithQuantityPack = (_additionalUnitSku$in = additionalUnitSku.includedWithQuantityPack) !== null && _additionalUnitSku$in !== void 0 ? _additionalUnitSku$in : 0;
  const includedWithProductVersion = (_additionalUnitSku$in2 = additionalUnitSku.includedWithProductVersion) !== null && _additionalUnitSku$in2 !== void 0 ? _additionalUnitSku$in2 : 0;
  const product = useAppSelector(state => productSelector(state, {
    productId
  }));
  const productLine = product === null || product === void 0 ? void 0 : product.productLine;
  const productLevel = product === null || product === void 0 ? void 0 : product.productLevel;
  const minPurchasableContacts = useAppSelector(state => minPurchasableContactsSelector(state, {
    quantityPackSkuId: additionalUnitSkuId
  })) || 0;
  const currencyCode = useDisplayedCurrencyCode();
  const maxAdditionalUnits = getMaxAdditionalUnits(additionalUnitSku);
  const maxContactsInPlan = maxAdditionalUnits + includedWithProductVersion;
  const steppingFunction = getQuantityPackSteppingFunction(additionalUnitSku);
  useEffect(() => {
    if (additionalUnitQuantity < minPurchasableContacts) {
      onChange(steppingFunction(minPurchasableContacts));
    } else if (steppingFunction(additionalUnitQuantity) !== additionalUnitQuantity) {
      onChange(steppingFunction(additionalUnitQuantity));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalUnitQuantity, minPurchasableContacts]);
  useEffect(() => {
    if (priceBreakdownVisible) {
      trackModalInteraction({
        action: 'click to open',
        name: 'contact-picker-more-details-modal',
        trackingSource: CREATE_A_BUNDLE,
        productLine: productLine !== null && productLine !== void 0 ? productLine : undefined,
        productLevel: isValidPaidProductLevel(productLevel) ? productLevel : undefined
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBreakdownVisible]);
  const {
    quantityPackPrice,
    loading: isLoadingStartingPOPPriceForContacts
  } = usePriceForQuantityPackQuantity({
    productApiName: product === null || product === void 0 ? void 0 : product.apiName,
    quantityPackApiName: 'additional-marketing-contacts',
    additionalQuantity: includedWithQuantityPack,
    // the packaged quantity for 1 quantityPack
    queryParamTerm: unitsPickerDisplayTerm
  });
  const displayStartingPrice = quantityPackPrice ? Number(quantityPackPrice.discountedPrice.monthlyPrice) : undefined;
  const minUnitsToDisplay = includedWithProductVersion + minPurchasableContacts;
  const handleContactsChange = debounce((newValue, roundUp) => {
    const localQuantity = steppingFunction(newValue - includedWithProductVersion, roundUp);
    const oldQuantity = steppingFunction(additionalUnitQuantity) + includedWithProductVersion;
    trackAdditionalUnitsPicker({
      action: `stepper ${localQuantity + includedWithProductVersion > oldQuantity ? 'increase' : 'decrease'}`,
      trackingSource: CREATE_A_BUNDLE,
      productLine: productLine !== null && productLine !== void 0 ? productLine : undefined,
      productLevel: isValidProductLevel(productLevel) ? productLevel : undefined,
      skuId: additionalUnitSkuId,
      displayedUnitQuantity: includedWithProductVersion + localQuantity
    });
    onChange(localQuantity);
  }, 500);
  const stepperId = useId();
  const marketingContactsTitleId = useId();
  const marketingContactsDescriptionId = useId();
  const includedMarketingContactsId = useId();
  const additionalMarketingContactsId = useId();
  const stepperAriaDescribedBy = [marketingContactsTitleId, marketingContactsDescriptionId, includedMarketingContactsId, additionalMarketingContactsId].join(' ');
  const paymentTerm = useAppSelector(planBuilderPaymentTermForProductsSelector) || 'monthly';
  const includedContacts = steppingFunction(additionalUnitQuantity) + includedWithProductVersion;
  const planBuilderIsLoadingData = useAppSelector(planBuilderIsLoadingSelector);
  if (!productLevel) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Divider, {}), priceBreakdownVisible && /*#__PURE__*/_jsx(AsyncContactsPriceBreakdownModal, {
      quantityPackSkuId: additionalUnitSkuId,
      handleModalClose: () => setPriceBreakdownVisible(false),
      queryParamTerm: paymentTerm
    }), /*#__PURE__*/_jsxs(TFlex, {
      direction: "row",
      justify: "between",
      children: [/*#__PURE__*/_jsx(FormLabel, {
        htmlFor: stepperId,
        children: /*#__PURE__*/_jsx(Big, {
          weight: "bold",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: `pricingPage.contactsPicker.select_jsx`,
            elements: {
              Link: ({
                children
              }) => /*#__PURE__*/_jsx(Link, {
                onClick: () => setPriceBreakdownVisible(true),
                onMouseEnter: () => void AsyncContactsPriceBreakdownModal.preload(),
                children: children
              })
            }
          })
        })
      }), /*#__PURE__*/_jsx(StepperInput, {
        id: stepperId,
        "aria-describedby": stepperAriaDescribedBy,
        stepSize: includedWithQuantityPack,
        value: includedContacts,
        min: minUnitsToDisplay,
        max: maxContactsInPlan,
        disabled: planBuilderIsLoadingData,
        onChange: event => handleContactsChange(parseInt(event.target.value, 10), true),
        "data-selenium-id": "contacts-picker-input"
      })]
    }), /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      as: "div",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        as: "span",
        children: [/*#__PURE__*/_jsx("span", {
          id: includedMarketingContactsId,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.contactsPicker.marketingContacts.includedContacts",
            options: {
              includedContacts
            }
          })
        }), /*#__PURE__*/_jsx("span", {
          id: additionalMarketingContactsId,
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            heights: [20],
            widths: ['100%'],
            isLoading: isLoadingStartingPOPPriceForContacts || displayStartingPrice === undefined,
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "pricingPage.contactsPicker.marketingContacts.additionalContacts_jsx",
              elements: {
                QuantityMultiplier: () => /*#__PURE__*/_jsx(FormattedNumber, {
                  value: includedWithQuantityPack
                }),
                Price: () => /*#__PURE__*/_jsx(PriceDisplay, {
                  price: displayStartingPrice,
                  currencyCode: currencyCode
                })
              }
            })
          })
        })]
      })
    })]
  });
};
export default QuantityPickerContacts;